import React from "react";

import { RowShell } from "../editor/MatchRow";
import { DECISION, ROW_STATE } from "../editor/matchModel";
import { STORED_MATCH_TEXT as T } from "./catalogStatsText";

const FACT = "fact";
const LEARNED = "learned";
const PERCENT = 100;
const SKELETON_TONE = "computing";

export const isReused = (line) => Boolean(line) && line.decision === DECISION.EXISTING;
const toneOf = (line) => (isReused(line) ? ROW_STATE.REUSED : ROW_STATE.NEW);

/** A small text tag with its longer explanation as the tooltip. */
const Tag = ({ label, hint, tone = "muted" }) => <span className={`le-tag le-tag--${tone}`} title={hint}>{label}</span>;

/** The editor's tags of the line as it was saved: Fact / Note / Symptom / Step, and Unsure when hedged. */
function Provenance({ line }) {
  const source = T.source[line.line_source];
  return (
    <>
      {source && <Tag label={source} hint={T.sourceHint[line.line_source]} />}
      {line.hedged && <Tag label={T.unsure} hint={T.unsureHint} tone="warn" />}
    </>
  );
}

/** Under the text: what the line reused (with its kind, origin and similarity) or that it was added as new, plus the engineer's override. */
function Sub({ line }) {
  const reused = isReused(line);
  const kind = line.selected_kind || line.kind;
  let key = T.addedNew;
  if (reused) key = line.label ? (kind === FACT ? T.reusesFact(line.label) : T.reuses(line.label)) : T.reusedNoLabel;
  const similar = reused && typeof line.score === "number" ? Math.round(line.score * PERCENT) : null;
  const origin = reused && line.source ? (line.source === LEARNED ? T.learned : T.fromRunbook) : "";
  return (
    <>
      <span className="le-mrow-sub-key">{key}</span>
      {reused && T.kind[kind] && <Tag label={T.kind[kind]} />}
      {origin && <Tag label={origin} />}
      {similar !== null && <Tag label={T.similar(similar)} hint={T.similarHint} />}
      <Provenance line={line} />
      {line.user_changed && <Tag label={T.changed} hint={T.changedHint(line.suggested_decision === DECISION.NEW)} tone="warn" />}
    </>
  );
}

/**
 * One line of a stored match snapshot, read-only, with the Review screen's row anatomy: the state glyph, the line and what it
 * reused beneath, and the state as a static chip where the Review screen has its match control. `line` is a snapshot row
 * ({text, decision, label, score, source, selected_kind, line_source, hedged, user_changed, ...}); every field may be absent.
 */
export default function StoredMatchRow({ line }) {
  const tone = toneOf(line);
  return (
    <RowShell tone={tone} text={line.text || ""} sub={<Sub line={line} />}
      control={<span className={`le-pill le-pill--${tone}`}>{T.state[tone]}</span>} />
  );
}

/** A row-shaped placeholder with the same geometry, for a section still loading. */
export function SkeletonRow() {
  return (
    <RowShell tone={SKELETON_TONE} icon={null} text={<span className="le-mrow-sub-skel le-mrow-text-skel" aria-hidden="true" />}
      sub={<span className="le-mrow-sub-skel" aria-hidden="true" />} control={<span className="le-mbtn le-mbtn--skel" role="presentation" />} />
  );
}
